/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import ServerSideTable from 'components/common/advance-table/ServerSideTable';
import ServerSideTableWrapper from 'components/common/advance-table/ServerSideTableWrapper';
import ServerSideTableFooter from 'components/common/advance-table/ServerSideTableFooter';
import { get, isEmpty } from 'lodash';
import { getWeatherReportdata } from 'api/endpoints/Weather';
import useApi from 'hooks/useApi';
import dayjs from 'dayjs';
import DatePicker from 'components/dashboards/manage-projects/DatePicker';
import { getTenantFromToken } from 'utils/routeUtils';

const FarmLevelReportTable = ({
  weatherData,
  showFilter,
  setExportData,
  selectedDistrict,
  limit = 25,
  showHeader = true,
  showFooter = false,
  selectedState
}) => {
  const tenant = getTenantFromToken();
  const getWeatherReportDataApi = useApi(getWeatherReportdata);

  const [tableData, setTableData] = useState(weatherData || {});
  const [selectedData, setSelectedData] = useState([]);
  const [pageSize, setPageSize] = useState(limit);
  const [pageIndex, setPageIndex] = useState(0);
  const [cursor, setCursor] = useState(0);
  const [filters, setFilters] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [variant, setVariant] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [globalSearch, setGlobalSearch] = useState('');

  const [canNextPage, setCanNextPage] = useState(true);
  const [paginationFilter, setPaginationFilter] = useState(false);

  useEffect(() => {
    let limit = pageSize;
    let cursor = pageIndex * pageSize;
    let district_ids = selectedDistrict.map(d => d.value);
    setCursor(cursor);
    const state_ids = selectedState?.value ? [selectedState.value] : [];
    getWeatherReportDataApi.request({
      cursor,
      limit: pageSize,
      filter: globalSearch,
      district_ids,
      state_ids
    });
  }, [pageIndex, filters, pageSize, globalSearch]);

  useEffect(() => {
    const tabledata = get(getWeatherReportDataApi, 'data.results', []);

    // Safely extract values with defensive checks
    const variants = tabledata?.variants || []; // Default to an empty array if undefined
    const firstVariant = variants[0]; // Safely get the first variant
    const selectedData = firstVariant ? tabledata?.data?.[firstVariant] : []; // Safely get the corresponding data

    // Update state based on extracted values
    setRowCount(get(getWeatherReportDataApi, 'data.results.total', 0));
    setCanNextPage(tabledata?.remaining_records > 0);
    setTableData(tabledata || []);
    setSelectedData(selectedData || []);
    setVariant(variants || []);
    setSelectedDate(firstVariant || null);
    setExportData(selectedData);
  }, [getWeatherReportDataApi.data]);

  // Handle variant change
  const handleVariantChange = value => {
    setSelectedDate(value);
    const newDate = dayjs(value)?.format('DD-MM-YYYY');
    setSelectedData(tableData?.data?.[newDate] || []); // Update data based on the selected variant
    setExportData(tableData?.data?.[newDate]);
    setPageIndex(0); // Reset pagination
  };

  const dates = variant.map(dateStr => {
    const [day, month, year] = dateStr.split('-');
    return new Date(`${year}-${month}-${day}`);
  });
  const minSelectableDate = dates[0];
  const maxSelectableDate = dates[dates.length - 1];

  // Filter table data client-side
  const filteredData = useMemo(() => {
    let filtered = selectedData;

    // Apply column filters
    Object.entries(filters).forEach(([columnId, filterValue]) => {
      if (filterValue) {
        filtered = filtered.filter(row => {
          const cellValue = row[columnId]?.toString().toLowerCase() || '';
          return cellValue.includes(filterValue.toLowerCase());
        });
      }
    });

    const hasValidFilter = Object.values(filters).some(
      value => value !== null && value !== ''
    );
    hasValidFilter ? setPaginationFilter(true) : setPaginationFilter(false);

    return filtered;
  }, [selectedData, filters]);

  useEffect(() => {
    if (!showFilter) {
      const clearedFilters = Object.keys(filters).reduce((acc, key) => {
        acc[key] = ''; // Reset each filter to an empty string
        return acc;
      }, {});
      setFilters(clearedFilters);
    }
  }, [showFilter]);

  // Define table columns dynamically
  const columns = useMemo(() => {
    if (!selectedData || selectedData.length === 0 || !selectedData[0])
      return [];

    return Object.keys(selectedData[0])
      .filter(
        key => !key.includes('_color') && key !== 'farm_id' && key !== 'id'
      )
      .map((key, index) => ({
        Header: key
          .replace(/_/g, ' ')
          .replace(/(^\w|\b\w(?![^()]*\)))/g, c => c.toUpperCase()),
        accessor: key,
        isSticky: index === 1 || index === 2,
        Cell: ({ value, row }) => {
          // Dynamically handle NA or null values
          const colorKey = `${key}_color`;
          let displayValue =
            typeof value === 'object' && value !== null
              ? ' - '
              : value === 'NA' || value === null
              ? ' - '
              : value;

          if (key === 'farm_name') {
            return (
              <span
                style={{
                  color: 'blue',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                {displayValue || ''}
              </span>
            );
          }

          return (
            <span style={{ color: row.original[colorKey] || 'inherit' }}>
              {displayValue || ''}
            </span>
          );
        },
        Filter: ({ column }) => (
          <input
            type="text"
            placeholder={`Filter ${key}`}
            className="form-control"
            value={filters[key] || ''}
            onChange={e => handleFilterChange(key, e.target.value)}
          />
        )
      }));
  }, [selectedData, filters]);

  // Handle filter changes
  const handleFilterChange = (columnId, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [columnId]: value
    }));
  };

  // Handle pagination
  const handlePageChange = newPageIndex => {
    setPageIndex(newPageIndex);
  };

  const handlePageSizeChange = newPageSize => {
    setPageSize(newPageSize);
    setPageIndex(0);
  };

  const handleGlobalSearch = e => {
    const searchValue = e.target.value;
    setGlobalSearch(searchValue);
  };

  return (
    <ServerSideTableWrapper
      key={`${pageSize}-${pageIndex}`}
      columns={columns}
      data={filteredData}
      pagination
      sortable
      perPage={pageSize}
      className="min-vh-50 py-5"
      stickyColumns={[]}
    >
      {showHeader ? (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <input
            type="text"
            placeholder="Search..."
            className="form-control w-25"
            value={filters.search_text}
            onChange={handleGlobalSearch}
          />
          <div className="d-flex gap-3">
            <DatePicker
              selected={
                selectedDate instanceof Date ? selectedDate : new Date()
              }
              onChange={newValue => handleVariantChange(newValue)}
              placeholderText="Start Date"
              className="form-control"
              dateFormat="dd-MM-yyyy"
              minDate={minSelectableDate}
              maxDate={maxSelectableDate}
            />
          </div>
        </div>
      ) : (
        <></>
      )}

      <ServerSideTable
        key={`${pageSize}-${pageIndex}`}
        table
        headerClassName="bg-200 text-900 text-nowrap align-middle"
        rowClassName="align-middle white-space-nowrap"
        tableProps={{
          bordered: true,
          striped: true,
          className: 'fs--1 mb-0'
        }}
        filters={filters}
        handleFilterChange={handleFilterChange}
        showFilterRow={showFilter}
        pageIndex={pageIndex}
        pageSize={pageSize}
        clientSideSearch={true}
      />

      {showFooter ? (
        <div className="mt-3">
          <ServerSideTableFooter
            rowCount={filteredData?.length}
            pageSize={pageSize}
            pageIndex={pageIndex}
            paginationFilter={paginationFilter}
            setPageSize={handlePageSizeChange}
            canPreviousPage={pageIndex > 0}
            canNextPage={canNextPage}
            nextPage={() => handlePageChange(pageIndex + 1)}
            previousPage={() => handlePageChange(Math.max(0, pageIndex - 1))}
            rowsPerPageSelection
            navButtons
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </div>
      ) : (
        <></>
      )}
    </ServerSideTableWrapper>
  );
};

FarmLevelReportTable.propTypes = {
  // weatherData: PropTypes.object.isRequired,
  showFilter: PropTypes.bool.isRequired,
  setExportData: PropTypes.func.isRequired,
  selectedDistrict: PropTypes.array.isRequired
};

export default FarmLevelReportTable;
